// features/premium/PremiumPage.tsx — Premium page (auth-gated, Free/Premium preview states).
import { useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { useSession, useBilling } from '@/hooks';
import { useRightRail, RailGroup, RailPanel } from '@/app/shell/RightRail';
import { useToast, Button } from '@/ui';
import { Icon } from '@/icons';
import { PreviewToggle } from './PreviewToggle';
import type { PreviewTier } from './PreviewToggle';
import { FreeView } from './FreeView';
import { SubscribedView } from './SubscribedView';
import { FreeTierPanel } from './panels/FreeTierPanel';
import { PremiumTierPanel } from './panels/PremiumTierPanel';
import { startCheckout } from './checkoutFlow';
import { billingService } from '@/core/services/billingService';

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;
const Title = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;
const PreviewNote = styled.p`
  margin: 4px 0 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const StatusPanel = styled.div`
  padding: 20px;
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`;
const StatusText = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin: 0 0 14px;
  padding: 10px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: color-mix(in srgb, ${({ theme }) => theme.color.accentAmber} 14%, transparent);
  border: 1px solid transparent;
`;
const BarText = styled.span`
  flex: 1;
  min-width: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;
const EnableLink = styled(Link)`
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.accentCyan};
  background: color-mix(in srgb, ${({ theme }) => theme.color.accentCyan} 14%, transparent);
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 5px 12px;
  text-decoration: none;
  white-space: nowrap;
`;
const Dismiss = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  line-height: 0;
  color: ${({ theme }) => theme.color.textMuted};
`;

export function PremiumPage() {
  const { isAuthenticated, currentUser } = useSession();
  const { billing, state: billingState, refresh } = useBilling();
  const toast = useToast();
  // Fix-round F4(c): UNKNOWN is not "free". `billing` is null while the fetch
  // is in flight or after it failed, and this page must not render the live
  // upgrade path in either case — a paying member shown "Upgrade to Pro"
  // because /billing/me/ hiccuped is one click from a second subscription.
  const realTier = billing?.tier;
  const tierKnown = billingState === 'ready';
  // Fix-round F1: set once Checkout answers "billing_not_configured". There is
  // no Payment-Link fallback any more (wrong price, unattributable), so the
  // upgrade control degrades to the disabled "Supporter link coming soon"
  // affordance rather than sending anyone to a payment URL.
  const [billingUnavailable, setBillingUnavailable] = useState(false);
  const [twoFactorPromptDismissed, setTwoFactorPromptDismissed] = useState(false);
  // PreviewToggle NEVER writes anything (see PreviewToggle.tsx) — it only
  // changes which state is RENDERED. It's offered ONLY to an actual Pro
  // subscriber (so they can see what a free member sees); a free member
  // cannot "preview" Premium here, because doing so would mean showing
  // SubscribedView with no real billing data behind it — exactly the kind
  // of faked entitlement the brief rules out. A free member's only path to
  // the Pro view is actually subscribing.
  //
  // No effect-driven setState here (that cascades an extra render) — this
  // is a pure derivation: `null` means "no manual toggle yet, follow the
  // real tier"; a Popover click sets an explicit override for the rest of
  // this mount.
  const [manualPreview, setManualPreview] = useState<PreviewTier | null>(null);
  const previewTier: PreviewTier = manualPreview ?? (realTier === 'pro' ? 'premium' : 'free');

  const rightRail = useMemo(
    () => (
      <RailGroup pageKey="premium">
        {previewTier === 'premium' ? (
          <RailPanel id="tier" label="Plan overview">
            <PremiumTierPanel />
          </RailPanel>
        ) : (
          <RailPanel id="tier" label="Plan overview">
            <FreeTierPanel />
          </RailPanel>
        )}
      </RailGroup>
    ),
    [previewTier],
  );
  useRightRail(rightRail);

  if (!isAuthenticated || !currentUser) {
    return <Navigate to={`/signin?next=${encodeURIComponent('/premium')}`} replace />;
  }

  async function handleUpgrade() {
    const outcome = await startCheckout();
    if (outcome.kind === 'unavailable') {
      setBillingUnavailable(true);
      toast("Checkout isn't set up yet. Try again soon.");
    } else if (outcome.kind === 'already_subscribed') {
      // The server refused with 409 because this account already holds Pro.
      // Nothing was charged — the server is the gate — so this is not an error
      // and "please try again" would be a lie: no retry can ever succeed. The
      // real defect is that our copy of the tier was stale, which is exactly
      // why an upgrade control was on screen; refresh() re-reads /billing/me/
      // so the page stops offering something they already own.
      toast("You're already on Pro. Your subscription is active.");
      refresh();
    } else if (outcome.kind === 'error') {
      toast('Something went wrong starting checkout. Please try again.');
    }
    // 'redirected' navigates the tab away — nothing to update here, the user
    // is leaving the page.
  }

  async function handleManageBilling() {
    const url = await billingService.portal().catch(() => null);
    if (url) {
      window.location.href = url;
    } else {
      toast("Billing management isn't available right now.");
    }
  }

  // Fix-round F20 (web half): a supporter who hasn't enabled 2FA has paid for
  // a badge they can't see. Say so once, plainly, and say what fixes it —
  // and never imply the subscription itself is at risk, because it isn't.
  // Only an explicit `false` counts: the field is absent on payloads that
  // don't carry it, and "unknown" must not nag.
  const showTwoFactorPrompt =
    realTier === 'pro' && currentUser.twoFactorEnabled === false && !twoFactorPromptDismissed;

  return (
    <section>
      <Head>
        <Title>Premium</Title>
        {realTier === 'pro' && <PreviewToggle value={previewTier} onChange={setManualPreview} />}
      </Head>
      {showTwoFactorPrompt && (
        <Bar role="status">
          <Icon name="shield" size={16} />
          <BarText>
            Turn on two-factor authentication to display your supporter badge. Your Pro subscription
            is active either way. This only affects the badge.
          </BarText>
          <EnableLink to="/settings/privacy">Enable 2FA</EnableLink>
          <Dismiss
            type="button"
            aria-label="Dismiss"
            onClick={() => setTwoFactorPromptDismissed(true)}
          >
            <Icon name="close" size={16} />
          </Dismiss>
        </Bar>
      )}
      {realTier === 'pro' && previewTier === 'free' && (
        <PreviewNote>
          Previewing what a free member sees. This doesn&apos;t change your plan.
        </PreviewNote>
      )}
      {!tierKnown ? (
        // Neutral while UNKNOWN — no plan cards, no upgrade button, no claim
        // about which tier this account is on.
        <StatusPanel role="status">
          {billingState === 'error' ? (
            <>
              <StatusText>We couldn&apos;t load your plan right now.</StatusText>
              <Button variant="secondary" onClick={refresh}>
                Try again
              </Button>
            </>
          ) : (
            <StatusText>Checking your plan…</StatusText>
          )}
        </StatusPanel>
      ) : previewTier === 'premium' && realTier === 'pro' ? (
        <SubscribedView
          displayName={currentUser.name ?? 'there'}
          onManageBilling={() => void handleManageBilling()}
        />
      ) : (
        <FreeView onUpgrade={() => void handleUpgrade()} billingUnavailable={billingUnavailable} />
      )}
    </section>
  );
}
